import { useIntl } from "react-intl";
import { useCallback, useEffect, useMemo, useState, useRef, ChangeEvent } from "react";
import {
	Combobox,
	Option,
	OptionGroup,
	Button,
	ComboboxOpenChangeData,
	ComboboxOpenEvents,
} from "@fluentui/react-components";
import { Flex, GapSize } from "@/components/atoms/flex";
import { TransferDestination, TransferDestinationType } from "../interactionTransferToIvr/types";
import { useStyle } from "./style";

export interface IvrTransferDestinationSearchProps {
	destinations: TransferDestination[];
	isLoading?: boolean;
	placeholder?: string;
	cancelText?: string;
	actionText?: string;
	actionIcon?: React.ReactElement;
	onCancel: () => void;
	onAction: (destination: TransferDestination) => void;
	showFlows?: boolean;
	queueFilter?: (queue: TransferDestination) => boolean;
}

export const IvrTransferDestinationSearch = (props: IvrTransferDestinationSearchProps) => {
	const {
		destinations,
		isLoading = false,
		placeholder,
		cancelText,
		actionText,
		actionIcon,
		onCancel,
		onAction,
		showFlows = false,
		queueFilter
	} = props;

	const intl = useIntl();
	const classes = useStyle();

	const [matchingQueues, setMatchingQueues] = useState<TransferDestination[]>([]);
	const [matchingFlows, setMatchingFlows] = useState<TransferDestination[]>([]);
	const [searchValue, setSearchValue] = useState<string>("");
	const selectedDestination = useRef<TransferDestination | undefined>();

	const setMatchingDestinations = useCallback(
		(matchingDestinations?: TransferDestination[]) => {
			const queues = matchingDestinations?.filter(
				(destination) => destination.type === TransferDestinationType.Queue
			) ?? [];
			
			const filteredQueues = queueFilter ? queues.filter(queueFilter) : queues;
			setMatchingQueues(filteredQueues);

			if (showFlows) {
				setMatchingFlows(
					matchingDestinations?.filter(
						(destination) => destination.type === TransferDestinationType.Flow
					) ?? []
				);
			}
		},
		[queueFilter, showFlows]
	);

	useEffect(() => {
		setMatchingDestinations(destinations);
	}, [destinations, setMatchingDestinations]);

	const onSearchChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setSearchValue(value);
			
			const trimmedValue = value.trim();
			const destinationsMatchingSearchTerm = destinations?.filter(
				(destination) =>
					destination.name
						.toLowerCase()
						.includes(trimmedValue.toLowerCase())
			);

			const includesCurrentlySelected = destinationsMatchingSearchTerm?.find(
				(destination) => destination.id === selectedDestination.current?.id
			);
			
			selectedDestination.current = includesCurrentlySelected;
			setMatchingDestinations(destinationsMatchingSearchTerm);
		},
		[destinations, setMatchingDestinations]
	);

	const onDestinationSelect = useCallback(
		(_: any, data: any) => {
			const destination = destinations?.find(d => d.id === data.optionValue);
			selectedDestination.current = destination;

			if (data.optionText) {
				setSearchValue(data.optionText);
			}

			setMatchingDestinations(destinations);
		},
		[destinations, setMatchingDestinations]
	);

	const onOpenChange = useCallback(
		(_: ComboboxOpenEvents, data: ComboboxOpenChangeData) => {
			if (data.open) {
				return;
			}

			if (selectedDestination.current) {
				setSearchValue(selectedDestination.current.name);
			}
		},
		[]
	);

	const handleAction = useCallback(() => {
		if (selectedDestination.current) {
			onAction(selectedDestination.current);
		}
	}, [onAction]);

	const flowsList = useMemo(() => {
		if (!showFlows || matchingFlows.length === 0) {
			return null;
		}

		return (
			<OptionGroup
				label={intl.formatMessage({ id: "Transfer.FlowsGroup" })}
			>
				{matchingFlows.map((flow) => (
					<Option
						key={flow.id}
						text={flow.name}
						value={flow.id}
						checkIcon={null}
						className={classes.optionRow}
					>
						{flow.name}
					</Option>
				))}
			</OptionGroup>
		);
	}, [matchingFlows, showFlows, intl]);

	return (
		<Flex column gap={GapSize.Medium}>
			<Combobox
				className={classes.combobox}
				placeholder={placeholder || intl.formatMessage({ id: "Transfer.TypeQueuePlaceholder" })}
				value={searchValue}
				onChange={onSearchChange}
				onOptionSelect={onDestinationSelect}
				onOpenChange={onOpenChange}
				disabled={isLoading}
				freeform
				inlinePopup
				listbox={{ className: classes.listbox }}
				// The list may only sit above or below the input. Fluent's own fallbacks include the two
				// sides, so a list that stops fitting below is thrown out beside the popover. Its autoSize
				// writes a max-height inline from the room around the input, which beats the cap the
				// listbox slot carries as a class; off, that cap is what decides the height. The popup
				// still takes the input's width, which matchTargetSize does and this does not touch.
				positioning={{
					position: "below",
					align: "start",
					fallbackPositions: ["above"],
					autoSize: false,
				}}
			>
				<OptionGroup
					label={intl.formatMessage({ id: "Transfer.QueuesGroup" })}
				>
					{matchingQueues.map((queue) => (
						<Option
							key={queue.id}
							text={queue.name}
							value={queue.id}
							checkIcon={null}
							className={classes.optionRow}
						>
							{queue.name}
						</Option>
					))}
				</OptionGroup>
				{flowsList}
			</Combobox>
			
			<Flex gap={GapSize.Small} hAlign="flex-end">
				<Button onClick={onCancel}>
					{cancelText || intl.formatMessage({ id: "Common.Cancel" })}
				</Button>
				<Button
					icon={actionIcon}
					appearance="primary"
					disabled={!selectedDestination.current}
					onClick={handleAction}
				>
					{actionText || intl.formatMessage({ id: "Transfer.ActionButton" })}
				</Button>
			</Flex>
		</Flex>
	);
};
